import React, { useState, useEffect, useCallback } from 'react';
import Container from '@mui/material/Container';
import TextField from '@mui/material/TextField';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import '../InvoicePage.css';
import { SERVER_URL } from '../../../constants.js'
import { formatLocalDateOnly } from '../../shared utils/dateFormatters';
import { generateStudentLetterPDF } from './utils/letterPdf';
import LetterPreview from './LetterPreview';
import dayjs from 'dayjs';
import 'dayjs/locale/en-gb';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';


function StudentSummary(props) {
  const { selectedPerson, selectedStay } = props;
  const [student, setStudent] = useState([]);

  const [invoiceDate, setInvoiceDate] = useState(dayjs());
  const [letterAddressee, setLetterAddressee] = useState('');

  const handleInvoiceDateChange = (event) => {
    setInvoiceDate(event.target.value);
  };

  const fetchStudent = useCallback(() => { 
    const token = sessionStorage.getItem('bearer');
    fetch(`${SERVER_URL}api/students/${selectedPerson.id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((response) => {
        if (response.status === 204) {
          return [];
        } else if (response.ok) {
          return response.json();
        } else {
          throw new Error('Failed to fetch student');
        }
      })
      .then((data) => {
        sessionStorage.setItem('bookings', JSON.stringify(data));
        setStudent(data);
      })
      .catch((err) => console.error(err));
  }, [selectedPerson.id]);

  useEffect(() => {
    fetchStudent();
  }, [fetchStudent]);

  const [bookings, setBookings] = useState([]);
  const fetchBookings = useCallback(() => {
    const token = sessionStorage.getItem('bearer');
    fetch(`${SERVER_URL}api/student/${selectedPerson.id}/booking`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((response) => {
        if (response.status === 204) {
          return [];
        } else if (response.ok) {
          return response.json();
        } else {
          throw new Error('Failed to fetch bookings');
        }
      })
      .then((data) => {
        const filteredData = data.filter(booking =>
          formatLocalDateOnly(new Date(booking.startDate)) >= formatLocalDateOnly(new Date(selectedStay.arrivalDate)) &&
          formatLocalDateOnly(new Date(booking.endDate)) <= formatLocalDateOnly(new Date(selectedStay.departureDate))
        );
        sessionStorage.setItem('bookings', JSON.stringify(filteredData));
        setBookings(filteredData);
      })
      .catch((err) => console.error(err));
  }, [selectedPerson.id]);
 
  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);
  const sortedBookings = [...bookings].sort((a, b) => {
    return new Date(a.startDate) - new Date(b.startDate);
  });

  const [stays, setStays] = useState([]);
  const fetchStays = useCallback(() => {
    const token = sessionStorage.getItem('bearer');
    fetch(`${SERVER_URL}api/student/${selectedPerson.id}/stays`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((response) => {
        if (response.status === 204) {
          return [];
        } else if (response.ok) {
          return response.json();
        } else {
          throw new Error('Failed to fetch stays');
        }
      })
      .then((data) => {
        const filteredData = data.filter(stay =>
          formatLocalDateOnly(new Date(stay.arrivalDate)) >= formatLocalDateOnly(new Date(selectedStay.arrivalDate)) &&
          formatLocalDateOnly(new Date(stay.departureDate)) >= formatLocalDateOnly(new Date(selectedStay.departureDate))
        );

        sessionStorage.setItem('stays', JSON.stringify(filteredData));
        setStays(filteredData);
      })
      .catch((err) => console.error(err));
  }, [selectedPerson.id]);
 
  useEffect(() => {
    fetchStays();
  }, [fetchStays]);
  const sortedStays = [...stays].sort((a, b) => {
    return new Date(a.arrivalDate) - new Date(b.arrivalDate);
  });

  const [transfers, setTransfers] = useState([]);
  const fetchTransfers = useCallback(() => {
    const token = sessionStorage.getItem('bearer');
    fetch(`${SERVER_URL}api/transfers/${selectedPerson.id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((response) => {
        if (response.status === 204) {
          return [];
        } else if (response.ok) {
          return response.json();
        } else {
          throw new Error('Failed to fetch transfers');
        }
      })
      .then((data) => {

        const filteredData = data.filter(transfer => 
          formatLocalDateOnly(new Date(transfer.transferDate)) >= formatLocalDateOnly(new Date(selectedStay.arrivalDate))
        );
        sessionStorage.setItem('stays', JSON.stringify(filteredData));
        setTransfers(filteredData);
      })
      .catch((err) => console.error(err));
  }, [selectedPerson.id]);
 
  useEffect(() => {
    fetchTransfers();
  }, [fetchTransfers]);
  const sortedTransfers = [...transfers].sort((a, b) => {
    return new Date(a.transferDate) - new Date(b.transferDate);
  });
  
  let blissLogoPath;
  blissLogoPath = process.env.PUBLIC_URL + "/assets/img/blissLogo.png";

  let summary;
  let summaryalt;
  summary = process.env.PUBLIC_URL + "/assets/img/summary.png";
  summaryalt="Summary";
  
  let footer;
  footer = process.env.PUBLIC_URL + "/assets/img/footer.png";

  const drawBrandAccentLabel = (pdf, label, value, x, y, brandPink, brandNavy) => {
  pdf.setFont('helvetica', 'bold');

  const first = label.slice(0, 2);
  const rest = label.slice(2);

  // Draw coloured label
  pdf.setTextColor(...brandPink);
  pdf.text(first, x, y);

  const offset1 = pdf.getTextWidth(first);

  pdf.setTextColor(...brandNavy);
  pdf.text(rest + ': ', x + offset1, y);

  const offset2 = pdf.getTextWidth(rest + ': ');

  // Draw value (normal colour)
  pdf.setTextColor(40);
  pdf.text(String(value || ''), x + offset1 + offset2, y);
};

  const drawBrandAccentText = (pdf, text, xCenter, y, fontSize, brandPink, brandNavy) => {
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(fontSize);

  const words = text.split(' ');
  const parts = [];

  words.forEach((word, wordIndex) => {
    if (!word) return;

    if (word.length >= 2) {
      parts.push({ text: word.slice(0, 2), color: brandPink });
      if (word.length > 2) {
        parts.push({ text: word.slice(2), color: brandNavy });
      }
    } else {
      parts.push({ text: word, color: brandPink });
    }

    if (wordIndex < words.length - 1) {
      parts.push({ text: ' ', color: brandNavy });
    }
  });

  const totalWidth = parts.reduce((sum, part) => sum + pdf.getTextWidth(part.text), 0);
  let x = xCenter - totalWidth / 2;

  parts.forEach((part) => {
    pdf.setTextColor(...part.color);
    pdf.text(part.text, x, y);
    x += pdf.getTextWidth(part.text);
  });
};

  const generatePDF2 = (invoiceDate, studentId) => {
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'pt',
      format: 'a4',
    });

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    let y = 40;

    const brandNavy = [26, 31, 113];
    const brandPink = [230, 0, 126];
    const brandTeal = [60, 120, 130];
    const lightGrey = [245, 245, 245];

    const logo = process.env.PUBLIC_URL + "/assets/img/blissLogo.png";
    const footerImg = process.env.PUBLIC_URL + "/assets/img/footer.png";

    const tableDefaults = {
      theme: 'grid',
      margin: { left: 40, right: 40 },
      styles: {
        fontSize: 10,
        cellPadding: 6,
        textColor: 40,
        lineColor: [180, 180, 180],
        lineWidth: 0.5,
      },
      headStyles: {
        fillColor: brandNavy,
        textColor: 255,
        fontStyle: 'bold',
      },
      alternateRowStyles: {
        fillColor: lightGrey,
      },
    };

    // Header
const logoWidth = 50;
const logoHeight = 60;

// Draw logo (left)
pdf.addImage(logo, 'PNG', 40, y, logoWidth, logoHeight);

// "Booking Summary" on the right (aligned with logo)
drawBrandAccentText(
  pdf,
  'Booking Summary',
  pageWidth - 120,          // move further right
  y + logoHeight / 2 + 8,   // tiny vertical tweak
  20,                       // bigger text
  brandPink,
  brandNavy
);

// Divider line under header
const lineY = y + logoHeight + 10;

pdf.setDrawColor(...brandNavy);
pdf.setLineWidth(1);
pdf.line(40, lineY, pageWidth - 40, lineY);

// Move cursor below line
y = lineY + 18;

// Student meta (left/right)
pdf.setFontSize(11);

drawBrandAccentLabel(
  pdf,
  'Student ID',
  student.id || '',
  40,
  y,
  brandPink,
  brandNavy
);

const formattedPdfDate =
  invoiceDate && invoiceDate.format
    ? invoiceDate.format('DD-MM-YYYY')
    : invoiceDate;

drawBrandAccentLabel(
  pdf,
  'Date',
  formattedPdfDate,
  pageWidth - 120,
  y,
  brandPink,
  brandNavy
);

// spacing before tables
y += 25;

  autoTable(pdf, {
    ...tableDefaults,
    startY: y,
    head: [[
  {
    content: 'Student Details',
    colSpan: 4,
    styles: { halign: 'left' }
  }
]],
    body: [
      ['First Name', student.studentName || '', 'Surname', student.studentSurname || ''],
      ['Nationality', student.studentNationality || '', 'English Level', student.englishLevel || ''],
      [
        'Age at camp',
        sortedStays.length > 0 ? String(calculateAgeAtCamp(student.studentDob, sortedStays[0].arrivalDate)) : 'N/A',
        'Gender',
        student.studentGender || ''
      ],
    ],
  });

  y = pdf.lastAutoTable.finalY + 18;

  drawBrandAccentText(pdf, 'Locations', pageWidth / 2, y, 12, brandPink, brandNavy);

  y += 8;

  autoTable(pdf, {
    ...tableDefaults,
    startY: y,
    head: [['Campus', 'Arrival', 'Departure']],
    body: sortedStays.length > 0
      ? sortedStays.map((stay) => [
          stay.campus || '',
          stay.arrivalDate ? formatLocalDateOnly(new Date(stay.arrivalDate)) : '',
          stay.departureDate ? formatLocalDateOnly(new Date(stay.departureDate)) : '',
        ])
      : [['No stays found', '', '']],
  });

  y = pdf.lastAutoTable.finalY + 18;

  drawBrandAccentText(pdf, 'Bookings', pageWidth / 2, y, 12, brandPink, brandNavy);

  y += 8;

  autoTable(pdf, {
    ...tableDefaults,
    startY: y,
    head: [['Activity', 'Start', 'Finish']],
    body: sortedBookings.length > 0
      ? sortedBookings.map((booking) => [
          booking.productName || '',
          booking.startDate ? formatLocalDateOnly(new Date(booking.startDate)) : '',
          booking.endDate ? formatLocalDateOnly(new Date(booking.endDate)) : '',
        ])
      : [['No bookings found', '', '']],
  });

  y = pdf.lastAutoTable.finalY + 18;

  autoTable(pdf, {
    ...tableDefaults,
    startY: y,
    head: [[
  {
    content: 'Student Requirements and Permissions',
    colSpan: 2,
    styles: { halign: 'left' }
  }
]],
    body: [
      ['Class Requirements', student.classRequirements || ''],
      ['Room Requirements', student.roomRequirements || ''],
      ['Allergies', student.allergies || ''],
      ['Notes', student.notes || ''],
      ['Emergency Contact', student.emergencyContact || 'N/A'],
      ['Mobile Phone Opt-Out', student.mobilePhoneOptOut ? 'Yes' : 'No'],
      ['Pool Permission', student.hasPoolPermission ? 'Yes' : 'No'],
      ['Photo Permission', student.hasPhotoPermission ? 'Yes' : 'No'],
      ['Medical Permission', student.hasMedicalPermission ? 'Yes' : 'No'],
      ['Hospital Permission', student.hasHospitalPermission ? 'Yes' : 'No'],
      ['Excursion Permission', student.hasExcursionPermission ? 'Yes' : 'No'],
      ['Activity Permission', student.hasActivityPermission ? 'Yes' : 'No'],
      ['Supervision Permission', student.hasSupervisionPermission ? 'Yes' : 'No'],
    ],
    columnStyles: {
      0: { cellWidth: 180, fontStyle: 'bold' },
      1: { cellWidth: 'auto' },
    },
  });

  y = pdf.lastAutoTable.finalY + 18;

  drawBrandAccentText(pdf, 'Flights', pageWidth / 2, y, 12, brandPink, brandNavy);

  y += 8;

  autoTable(pdf, {
    ...tableDefaults,
    startY: y,
    head: [['Direction', 'Date', 'Time', 'Destination', 'Flight']],
    body: sortedTransfers.length > 0
      ? sortedTransfers.map((transfer) => [
          transfer.direction || '',
          transfer.transferDate ? formatLocalDateOnly(new Date(transfer.transferDate)) : '',
          transfer.direction === 'IN' ? (transfer.arrivalTime || '') : (transfer.departureTime || ''),
          transfer.arrive || '',
          transfer.flightId || '',
        ])
      : [['No flights details found', '', '', '', '']],
  });

  const finalPage = pdf.getNumberOfPages();
  pdf.setPage(finalPage);

  pdf.setDrawColor(...brandNavy);
  pdf.setLineWidth(1);
  pdf.line(40, pageHeight - 70, pageWidth - 40, pageHeight - 70);

  pdf.addImage(footerImg, 'PNG', (pageWidth - 120) / 2, pageHeight - 55, 120, 35);

  const fullName = `${student.studentName || ''} ${student.studentSurname || ''}`.trim() || String(studentId);
  pdf.save(`Summary_${fullName}_${formattedPdfDate}.pdf`);
};

  const calculateAgeAtCamp = (dob, arrivalDate) => {
    const birthDate = new Date(dob);
    const arrival = new Date(arrivalDate);
    let age = arrival.getFullYear() - birthDate.getFullYear();
    const m = arrival.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && arrival.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

return (
<Container
  fixed
  style={{
    backgroundColor: 'white',
    maxWidth: '100%',
    fontFamily: 'Roboto, sans-serif',
    fontSize: '14px',
  }}
>
  <React.Fragment>
    <div style={{ overflowX: 'auto' }}>
    <div style={{ display: 'flex', flexWrap: 'nowrap', gap: '20px', alignItems: 'flex-start' }}>
      <div className="summary-sheet" style={{ flex: '1 1 420px', minWidth: '420px', order: 2 }}>
    <div id="report">
     <div className="images-container">
        <div className="logo-container" style={{ display: 'flex', justifyContent: 'space-between' }}>
          <img alt="Bliss" src={blissLogoPath} style={{ height: '80px' }} />
          <img alt={summaryalt} src={summary} style={{ height: '35px', marginTop: '35px' }} />
        </div>
        <hr className="line" style={{ width: '100%' }} />
      </div>
      <div className="student-details-container">
        <div><strong>{student.studentName} {student.studentSurname}</strong></div>
        <div>Student ID: {student.id}</div>
      </div>
      <div style={{ textAlign: 'right' }}>
        <p>Date: {invoiceDate ? invoiceDate.format('DD/MM/YYYY') : ''}</p>
      </div>
      <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
        <div style={{ textAlign: "center" }}>
          <h2>Booking Summary</h2>
        </div>
      </div>
      <div className="bookings-container">
      <table className="bookings-table">
  <tbody>
    <tr><td colspan="2" style={{fontWeight: 'bold'}}>First Name:</td><td>{student.studentName}</td><td style={{fontWeight: 'bold'}}>Surname:</td><td>{student.studentSurname}</td>
    <td colspan="2" style={{fontWeight: 'bold'}}>Nationality:</td><td>{student.studentNationality}</td></tr>
    <tr><td colspan="2" style={{fontWeight: 'bold'}}>English Level:</td><td>{student.englishLevel}</td>
    <td colspan="2" style={{fontWeight: 'bold'}}>Age at camp:</td><td>{sortedStays && sortedStays.length > 0 ? calculateAgeAtCamp(student.studentDob, sortedStays[0].arrivalDate) : 'N/A'}</td>
    <td style={{fontWeight: 'bold'}}>Gender:</td><td>{student.studentGender}</td></tr>
    <tr><td colSpan="8" className="summary-section">Locations</td></tr>
    <tr style={{fontWeight: 'bold'}}><td colSpan="2">Campus</td><td>Arrival</td><td>Departure</td><td colspan="4"></td></tr>
    {sortedStays && sortedStays.length > 0 ? (
      sortedStays.map((stay) => (
        <tr key={stay.stayId}>
          <td colSpan="2">{stay.campus}</td>
          <td>{formatLocalDateOnly(new Date(stay.arrivalDate))}</td>
          <td>{formatLocalDateOnly(new Date(stay.departureDate))}</td>
          <td colspan="4"></td>
        </tr>
      ))
    ) : (
      <tr>
        <td colSpan="8">No Stays found</td>
      </tr>
    )}
    <tr><td colSpan="8" className="summary-section">Bookings</td></tr>
    <tr style={{fontWeight: 'bold'}}><td colSpan="4">Activity</td><td colspan="2">Start</td><td colspan="2">Finish</td></tr>
    {sortedBookings && sortedBookings.length > 0 ? (
      sortedBookings.map((booking) => (
        <tr key={booking.bookingId}>
          <td colSpan="4">{booking.productName}</td>
          <td colSpan="2">{formatLocalDateOnly(new Date(booking.startDate))}</td>
          <td colSpan="2">{formatLocalDateOnly(new Date(booking.endDate))}</td>
        </tr>
      ))
    ) : (
      <tr>
        <td colSpan="8">No bookings found</td>
      </tr>
    )}
    <tr><td colSpan ="8"></td></tr>
    <tr><td colSpan="2" style={{fontWeight: 'bold'}}>Class Requirements:</td><td colSpan="6">{student.classRequirements}</td></tr>
    <tr><td colSpan="2" style={{fontWeight: 'bold'}}>Room Requirements:</td><td colSpan="6">{student.roomRequirements}</td></tr>
    <tr><td colSpan="2" style={{fontWeight: 'bold'}}>Allergies:</td><td colSpan="6">{student.allergies}</td></tr>
    <tr><td colSpan="2" style={{fontWeight: 'bold'}}>Notes:</td><td colSpan="6">{student.notes}</td></tr>
    <tr>
  <td colSpan="2" style={{ fontWeight: 'bold' }}>Emergency Contact:</td>
  <td colSpan="6">{student.emergencyContact || 'N/A'}</td>
</tr>
<tr>
  <td colSpan="2" style={{ fontWeight: 'bold' }}>Mobile Phone Opt-Out:</td>
  <td colSpan="6">{student.mobilePhoneOptOut ? 'Yes' : 'No'}</td>
</tr>
    <tr><td style={{fontWeight: 'bold'}}>Pool</td><td>{student.hasPoolPermission ? 'Yes' : 'No'}</td>
    <td style={{fontWeight: 'bold'}}>Photo</td><td>{student.hasPhotoPermission ? 'Yes' : 'No'}</td>
    <td style={{fontWeight: 'bold'}}>Medical</td><td>{student.hasMedicalPermission ? 'Yes' : 'No'}</td>
    <td style={{fontWeight: 'bold'}}>Hospital</td><td>{student.hasHospitalPermission ? 'Yes' : 'No'}</td></tr>
    <tr><td style={{fontWeight: 'bold'}}>Excursion</td><td>{student.hasExcursionPermission ? 'Yes' : 'No'}</td>
    <td style={{fontWeight: 'bold'}}>Activity</td><td>{student.hasActivityPermission ? 'Yes' : 'No'}</td>
    <td style={{fontWeight: 'bold'}}>Supervision</td><td>{student.hasSupervisionPermission ? 'Yes' : 'No'}</td><td colSpan="2"></td></tr>
    <tr><td colSpan="8" className="summary-section">Flights</td></tr>
    <tr style={{fontWeight: 'bold'}}><td>Direction</td><td colspan="2">Date</td><td>Time</td><td colspan="2">Destination</td><td colspan="2">Flight</td></tr>
    {sortedTransfers && sortedTransfers.length > 0 ? (
      sortedTransfers.map((transfer) => (
        <tr key={transfer.id}>
          <td>{transfer.direction}</td>
          <td colSpan="2">{formatLocalDateOnly(new Date(transfer.transferDate))}</td>
          <td>{transfer.direction === "IN" ? transfer.arrivalTime : transfer.departureTime}</td>
          <td colSpan="2">{transfer.arrive}</td>
          <td colSpan="2">{transfer.flightId}</td>
        </tr>
      ))
    ) : (
      <tr>
        <td colSpan="8">No bookings found</td>
      </tr>
    )}
  </tbody>
</table>

      </div>
      <hr className="line" />
      <div className="footer-container">
        <img alt="info@brownleeschools.com" src={footer} style={{ height: '80px', width: '200px', marginTop: '35px'}}/>
      </div>
    </div>
      </div>
      <div style={{ flex: '1 1 420px', minWidth: '420px', order: 1 }}>
        <LetterPreview
          student={student}
          stays={sortedStays}
          bookings={sortedBookings}
          letterDate={invoiceDate}
          addressee={letterAddressee}
        />
      </div>
    </div>
    </div>
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
  <DatePicker
    label="Summary Date"
    value={invoiceDate}
    onChange={(newValue) => setInvoiceDate(newValue)}
    format="DD/MM/YYYY"
    slotProps={{ textField: { size: 'small' } }}
  />
</LocalizationProvider>
    <TextField
      label="Letter addressee (optional)"
      value={letterAddressee}
      onChange={(e) => setLetterAddressee(e.target.value)}
      placeholder={`Parent / Guardian of ${`${student.studentName || ''} ${student.studentSurname || ''}`.trim() || 'our student'}`}
      size="small"
      sx={{ ml: 1, minWidth: 280 }}
      InputLabelProps={{ shrink: true }}
      helperText="Leave blank to address to the parent / guardian"
    />
    <button onClick={() => generateStudentLetterPDF({ student, stays: sortedStays, bookings: sortedBookings, letterDate: invoiceDate, addressee: letterAddressee })} type="button">Save Letter</button>
    <button onClick={() => generatePDF2(invoiceDate, student.id)} type="button" style={{ marginLeft: '8px' }}>Save Summary</button>
    <div>
        <p style={{ color: '#999999', fontSize: '10px' }}>Student: {selectedPerson.id}</p>
        <p style={{ color: '#999999', fontSize: '10px' }}>Is authenticated: {sessionStorage.getItem('isAuthenticated').toString()}</p>
      </div>
  </React.Fragment>
</Container>
  );
}

export default StudentSummary;
